import React, { ReactElement, useEffect, useMemo, useState } from "react"
import { useIntl } from "react-intl"
import { Analytics, layoutActions } from "@Techedge/digitalportal-shared"
import { connect } from "react-redux"
import { LuxModalFullScreen, useWindowResize } from "@lux/wardrobe-lib"
import { ForumTypeEnum, ForumUser, Thread } from "../models/forum/new-forum.model"
import { breadcrumb } from "../models/forum/breadcrumb.config"
import ForumCta from "../components/Forum/ForumCta"
import ForumTypeList from "../components/Forum/ForumTypeList"
import ForumThreadContainer from "../components/Forum/ForumThreadContainer"
import ForumTopicContainer from "../components/Forum/ForumTopicContainer"
import { createThread, createTopic, editThread, editTopic } from "../redux/actions/forum"
import { CreateThreadBody, CreateTopicBody, ForumService } from "../services/ForumService"
import { ForumCtaEnum } from "../models/forum/forum-cta"
import ForumTopicCta from "../components/Forum/ForumTopicCta"
import { getFiltersDomain } from "../redux/actions/filters"
import "./NewForum.scss"
import {setNewForumStoreList, setSmartFilterForum, setUserList} from "../redux/actions/new-forum"
import { NEW_FORUM_REDUCER_NAMESPACE } from "../redux/reducers/new-forum"
import { ProfileService } from "../services/ProfileService"
export interface NewForumProps {
    storeList: { store_id: string }[]
    userList: ForumUser[]
    type?: ForumTypeEnum
    id?: string
    threadId?: string
    smartFilter?: any
    setSmartFilter?: (value: any) => void
    onClose: () => void
    createThread: (body: CreateThreadBody, successLabel: string) => void
    editThread: (body: CreateThreadBody, successLabel: string) => void
    createTopic: (body: CreateTopicBody, successLabel: string) => void
    editTopic: (body: CreateTopicBody, successLabel: string) => void
    getFiltersDomain: () => void
    setLoading: (loading: boolean) => void
    setStoreList: (stores: { store_id: string }[]) => void
    setUserList: (userList: ForumUser[]) => void
}

const NewForum = (props: NewForumProps) => {
    const {
        storeList,
        userList,
        type,
        id,
        threadId,
        onClose,
        createThread,
        editThread,
        createTopic,
        editTopic,
        getFiltersDomain,
        setLoading,
        setStoreList,
        setUserList,
        smartFilter,
        setSmartFilter
    } = props
    const intl = useIntl()
    const isMobile = useWindowResize()
    const [activeType, setActiveType] = useState<ForumTypeEnum>(type || ForumTypeEnum.THREAD)
    const [isEdit] = useState<boolean>(!!type)
    const [title, setTitle] = useState<string>("")
    const [description, setDescription] = useState("")
    const [referenceThread, setReferenceThread] = useState<Thread>(null)
    const [referenceStoreList, setReferenceStoreList] = useState<any[]>([])
    const [topicStoreList, setTopicStoreList] = useState<any[]>([])
    const [referenceThreadUserList, setReferenceThreadUserList] = useState<ForumUser[]>([])

    const breadcrumbForum = useMemo(() => breadcrumb(intl, type), [breadcrumb])
    const mobileLabel = useMemo(
        () =>
            intl.formatMessage({
                id: `modals.new.forum.header.mobile${type ? `.edit.${type}` : ""}`,
                defaultMessage: "New discussion creation",
            }),
        [intl, type]
    )
    const createSuccessLabel = intl.formatMessage({
        id: `modals.new.forum.create.success.${activeType}`,
        defaultMessage: "Create successfully!",
    })
    const editSuccessLabel = intl.formatMessage({
        id: `modals.new.forum.edit.success.${activeType}`,
        defaultMessage: "Edit successfully!",
    })

    useEffect(() => {
        getFiltersDomain()
        if (id) {
            setLoading(true)
            if (type === ForumTypeEnum.THREAD) {
                ForumService.GetThread(id)
                    .then(({ data }) => {
                        setTitle(data?.title)
                        setDescription(data?.description)
                        setStoreList(data?.storeList.map((s) => ({ store_id: s.storeId })))
                        setUserList(data?.enabledUsers)
                        setSmartFilter(data?.smartFilter)
                    })
                    .finally(() => setLoading(false))
            }
            if (type === ForumTypeEnum.TOPIC) {
                ForumService.GetTopic(id, threadId)
                    .then(({ data }) => {
                        const topic = data?.topics[0]

                        setTitle(topic.title)
                        setDescription(topic.description)
                        setStoreList(topic.storeList.map((s) => ({ store_id: s.storeId })))
                        setUserList(topic.enabledUsers)
                        setReferenceThread({
                            _id: data._id,
                            title: data.title,
                        })
                        setSmartFilter(topic.smartFilter)
                    })
                    .finally(() => setLoading(false))
            }
        }

        return () => {
            setSmartFilter({})
        }
    }, [])

    useEffect(() => {
        if (referenceThread) {
            setLoading(true)
            ForumService.GetThread(referenceThread._id)
                .then((res) => {
                    setSmartFilter(res.data.smartFilter)
                    setReferenceStoreList(res.data.storeList.map((s) => ({ store_id: s.storeId })))
                    ProfileService.GetUserAvatars(res.data.enabledUsers.map((u) => u.userId)).then(
                        ({ data }) => {
                            const newUsers = res.data.enabledUsers.map((u) => ({
                                ...u,
                                attachment: {
                                    url: data.usersAvatars.find(
                                        (avatarUser) => avatarUser.userId === u.userId
                                    )?.attachmentUrl,
                                },
                            }))
                            setUserList(newUsers)
                            setReferenceThreadUserList(newUsers)
                        }
                    )
                })
                .finally(() => setLoading(false))
        }
    }, [referenceThread])

    useEffect(() => {
        setTitle("")
        setDescription("")
        setStoreList([])
        setTopicStoreList([])
        setReferenceThread(null)
        setReferenceStoreList([])
        setUserList([])
    }, [activeType])

    useEffect(() => {
        switch (activeType) {
            case ForumTypeEnum.THREAD:
                if (!type) {
                    Analytics._sendAnalyticsLinkEvent("Forum", "Thread:Creation", "Static")
                    return
                }
                Analytics._sendAnalyticsLinkEvent("Forum", "Thread:Edit", "Static")
                return
            case ForumTypeEnum.TOPIC:
                if (!type) {
                    Analytics._sendAnalyticsLinkEvent("Forum", "Thread:Topic:Add", "Static")
                    return
                }
                Analytics._sendAnalyticsLinkEvent("Forum", "Thread:Topic:Edit", "Static")
                return
        }
    }, [activeType, type])

    const onCta = (action: string) => {
        switch (action) {
            case ForumCtaEnum.CREATE_THREAD:
                createThread(
                    {
                        title,
                        description,
                        smartFilter: smartFilter,
                        storeIdList: storeList.map((s) => s.store_id),
                        enabledUsers: userList.map((u) => ({
                            firstname: u.firstname,
                            lastname: u.lastname,
                            nickname: u.nickname,
                            userId: u.userId,
                        })),
                    },
                    createSuccessLabel
                )
                return
            case ForumCtaEnum.CREATE_TOPIC:
                createTopic(
                    {
                        title,
                        description,
                        smartFilter: smartFilter,
                        storeIdList: topicStoreList.map((s) => s.store_id),
                        enabledUsers: userList
                            .filter((u) => !u.disabled)
                            .map((u) => ({
                                firstname: u.firstname,
                                lastname: u.lastname,
                                nickname: u.nickname,
                                userId: u.userId,
                            })),
                        referenceThreadId: referenceThread._id,
                    },
                    createSuccessLabel
                )
                return
            case ForumCtaEnum.EDIT_THREAD:
                editThread(
                    {
                        id,
                        title,
                        description,
                        smartFilter: smartFilter,
                        storeIdList: storeList.map((s) => s.store_id),
                        enabledUsers: userList.map((u) => ({
                            firstname: u.firstname,
                            lastname: u.lastname,
                            nickname: u.nickname,
                            userId: u.userId,
                        })),
                    },
                    editSuccessLabel
                )
                return
            case ForumCtaEnum.EDIT_TOPIC:
                editTopic(
                    {
                        id,
                        title,
                        description,
                        smartFilter: smartFilter,
                        storeIdList: storeList.map((s) => s.store_id),
                        enabledUsers: userList
                            .filter((u) => !u.disabled)
                            .map((u) => ({
                                firstname: u.firstname,
                                lastname: u.lastname,
                                nickname: u.nickname,
                                userId: u.userId,
                            })),
                        referenceThreadId: referenceThread._id,
                    },
                    editSuccessLabel
                )
                return
        }
    }
    const onReferenceThread = (thread: Thread) => {
        setReferenceThread(thread)
    }
    const getCtaTemplate = (): ReactElement => {
        if (activeType === ForumTypeEnum.TOPIC)
            return <ForumTopicCta disabled={!topicStoreList.length} onCta={onCta} type={type} />
        return <ForumCta onCta={onCta} type={type} />
    }
    const onRemoveReferenceUser = (id: string, enable?: boolean) => {
        setReferenceThreadUserList((value) => {
            return value.map((u) => ({
                ...u,
                disabled: u.userId === id ? !enable : u.disabled,
            }))
        })
    }
    const onTopicSetDistributionHandler = (storeList: any[]) => {
        setStoreList(storeList)
        setTopicStoreList(storeList)
    }

    return (
        <div className={`smart-modals-new-forum ${isMobile ? "mobile" : ""}`}>
            <LuxModalFullScreen
                footer={getCtaTemplate()}
                onClose={onClose}
                hideSidebar={true}
                breadcrumbMenu={breadcrumbForum}
                labelHeaderMobile={mobileLabel}>
                {!isEdit && <ForumTypeList onChangeType={setActiveType} activeType={activeType} />}
                {activeType === ForumTypeEnum.THREAD && (
                    <ForumThreadContainer
                        title={title}
                        description={description}
                        userList={userList}
                        storeList={storeList}
                        onSetTitle={setTitle}
                        onSetDescription={setDescription}
                        onSetDistribution={setStoreList}
                        onSetUsers={setUserList}
                        smartFilter={smartFilter}
                        setSmartFilter={setSmartFilter}
                    />
                )}
                {activeType === ForumTypeEnum.TOPIC && (
                    <ForumTopicContainer
                        title={title}
                        description={description}
                        userList={userList}
                        storeList={storeList}
                        onSetTitle={setTitle}
                        onSetDescription={setDescription}
                        onSetReference={onReferenceThread}
                        onSetDistribution={onTopicSetDistributionHandler}
                        onSetUsers={setUserList}
                        onRemoveReferenceUser={onRemoveReferenceUser}
                        referenceStoreList={referenceStoreList}
                        referenceThreadUserList={referenceThreadUserList}
                        referenceId={referenceThread?._id}
                        referenceTitle={referenceThread?.title}
                        id={id}
                        smartFilter={smartFilter}
                        setSmartFilter={setSmartFilter}
                    />
                )}
            </LuxModalFullScreen>
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    storeList: state[NEW_FORUM_REDUCER_NAMESPACE].storeList,
    userList: state[NEW_FORUM_REDUCER_NAMESPACE].userList,
    type: state.layoutReducer.modals.props?.type,
    id: state.layoutReducer.modals.props?.id,
    threadId: state.layoutReducer.modals.props?.threadId,
    smartFilter: state[NEW_FORUM_REDUCER_NAMESPACE].smartFilter,
})

const mapDispatchToProps = (dispatch: any) => ({
    onClose: () => {
        dispatch(
            layoutActions.setModals({
                type: null,
                props: { id: null, type: null },
            })
        )
    },
    createThread: (body: CreateThreadBody, successMessage: string) =>
        dispatch(createThread(body, successMessage)),
    editThread: (body: CreateThreadBody, successMessage: string) =>
        dispatch(editThread(body, successMessage)),
    createTopic: (body: CreateTopicBody, successMessage: string) =>
        dispatch(createTopic(body, successMessage)),
    editTopic: (body: CreateTopicBody, successMessage: string) =>
        dispatch(editTopic(body, successMessage)),
    getFiltersDomain: () => dispatch(getFiltersDomain()),
    setStoreList: (stores: { store_id: string }[]) => dispatch(setNewForumStoreList(stores)),
    setUserList: (userList: ForumUser[]) => dispatch(setUserList(userList)),
    setLoading: (loading: boolean) => dispatch(layoutActions.setModals({ props: { loading } })),
    setSmartFilter: (value: any) => dispatch(setSmartFilterForum(value)),
})

export default connect(mapStateToProps, mapDispatchToProps)(NewForum)
